import { useCallback, useEffect, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import "./VisualTemplateBodyEditor.css";

const TEMPLATE_EDITOR_SAFE_HTML_OPTIONS = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "textarea", "select"],
  FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover", "onfocus", "onmouseenter"],
};

export const sanitizeTemplateEditorHtml = (html) =>
  DOMPurify.sanitize(String(html || ""), TEMPLATE_EDITOR_SAFE_HTML_OPTIONS).trim();

export const templateHtmlToText = (html) => {
  const sanitizedHtml = sanitizeTemplateEditorHtml(html);
  if (!sanitizedHtml) return "";

  if (typeof document === "undefined") {
    return sanitizedHtml
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  const container = document.createElement("div");
  container.innerHTML = sanitizedHtml;
  container.querySelectorAll("br").forEach((node) => {
    node.replaceWith(document.createTextNode("\n"));
  });
  container.querySelectorAll("p, div, li, tr, h1, h2, h3, h4, h5, h6").forEach((node) => {
    node.appendChild(document.createTextNode("\n"));
  });

  return (container.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

export default function VisualTemplateBodyEditor({
  value,
  onChange,
  variables,
  readOnly = false,
}) {
  const editorRef = useRef(null);
  const sanitizedValue = useMemo(() => sanitizeTemplateEditorHtml(value), [value]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;
    if (editor.innerHTML !== sanitizedValue) {
      editor.innerHTML = sanitizedValue;
    }
  }, [sanitizedValue]);

  const emitChange = useCallback(() => {
    if (readOnly) return;
    const editor = editorRef.current;
    if (!editor) return;
    onChange(sanitizeTemplateEditorHtml(editor.innerHTML));
  }, [onChange, readOnly]);

  const runCommand = useCallback(
    (command, commandValue = null) => {
      if (readOnly) return;
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      document.execCommand(command, false, commandValue);
      emitChange();
    },
    [emitChange, readOnly]
  );

  const insertVariable = useCallback(
    (variable) => {
      runCommand("insertText", variable);
    },
    [runCommand]
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-2">
        <button
          type="button"
          className="h-8 min-w-8 rounded-lg border border-slate-300 bg-white px-2 text-sm font-bold text-slate-700 hover:bg-slate-100"
          disabled={readOnly}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand("bold")}
          title="Bold"
          aria-label="Bold"
        >
          B
        </button>
        <button
          type="button"
          className="h-8 min-w-8 rounded-lg border border-slate-300 bg-white px-2 text-sm italic text-slate-700 hover:bg-slate-100"
          disabled={readOnly}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand("italic")}
          title="Italic"
          aria-label="Italic"
        >
          I
        </button>
        <button
          type="button"
          className="h-8 min-w-8 rounded-lg border border-slate-300 bg-white px-2 text-sm underline text-slate-700 hover:bg-slate-100"
          disabled={readOnly}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand("underline")}
          title="Underline"
          aria-label="Underline"
        >
          U
        </button>
        <button
          type="button"
          className="h-8 min-w-8 rounded-lg border border-slate-300 bg-white px-2 text-base font-semibold text-slate-700 hover:bg-slate-100"
          disabled={readOnly}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand("insertUnorderedList")}
          title="Bulleted list"
          aria-label="Bulleted list"
        >
          &bull;
        </button>
        <button
          type="button"
          className="h-8 min-w-8 rounded-lg border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
          disabled={readOnly}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => runCommand("insertOrderedList")}
          title="Numbered list"
          aria-label="Numbered list"
        >
          1.
        </button>
        <div className="h-6 w-px bg-slate-300" />
        {(variables || []).map((variable) => (
          <button
            key={variable}
            type="button"
            className="h-8 rounded-lg border border-rose-200 bg-white px-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
            disabled={readOnly}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => insertVariable(variable)}
          >
            {variable}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        role="textbox"
        aria-label="Email message"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        className={`visualTemplateBodyEditorContent min-h-[220px] w-full overflow-auto rounded-xl border border-slate-300 px-3 py-2 text-sm leading-6 text-slate-800 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 ${
          readOnly ? "bg-slate-50" : "bg-white"
        }`}
        onInput={emitChange}
        onBlur={emitChange}
        onClickCapture={(event) => {
          if (event.target?.closest?.("a")) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      />
    </div>
  );
}
